'use client';

import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { FaHome, FaGamepad, FaFish, FaDice, FaGem, FaTrophy, FaStar, FaFire } from 'react-icons/fa';
import { useLanguage } from '@/contexts/LanguageContext';

interface CategorySectionProps {
  categories?: Array<{
    id: number;
    name: string;
    image: string;
    active: number;
  }>;
  imgPrefix?: string;
}

const CategorySection: React.FC<CategorySectionProps> = ({ 
  categories = [], 
  imgPrefix = '' 
}) => {
  const { t } = useLanguage();
  const pathname = usePathname();

  // Default categories as fallback
  const defaultCategories = [
    {
      id: 'home',
      href: '/',
      icon: FaHome,
      label: t('home'),
      key: 'home',
      isDefault: true
    },
    {
      id: 'slots',
      href: '/games?category=slots',
      icon: FaGamepad,
      label: t('slot_games'),
      key: 'slots',
      isDefault: true
    },
    {
      id: 'fish',
      href: '/games?category=fish',
      icon: FaFish,
      label: t('fish_shooting'),
      key: 'fish',
      isDefault: true
    },
    {
      id: 'live',
      href: '/games?category=live',
      icon: FaDice,
      label: t('live_casino'),
      key: 'live',
      isDefault: true
    },
    {
      id: 'lottery',
      href: '/lottery-2d',
      icon: FaGem,
      label: t('lottery'),
      key: 'lottery',
      isDefault: true
    },
    {
      id: 'sports',
      href: '/games?category=sports',
      icon: FaTrophy,
      label: t('sports'),
      key: 'sports',
      isDefault: true
    },
    {
      id: 'hot',
      href: '/games?category=hot',
      icon: FaFire,
      label: t('hot_games'),
      key: 'hot',
      isDefault: true
    },
    {
      id: 'new',
      href: '/games?category=new',
      icon: FaStar,
      label: t('new_games'),
      key: 'new',
      isDefault: true
    }
  ];

  // Map API categories to display format
  const apiCategories = categories
    .filter(cat => cat.active === 1)
    .map(cat => ({
      id: cat.id,
      href: `/games?category=${cat.id}`,
      label: cat.name,
      key: `category_${cat.id}`,
      image: cat.image,
      isDefault: false
    }));

  // Combine API categories with default categories
  const allCategories = [...apiCategories, ...defaultCategories];

  const isActive = (href: string, key: string) => {
    if (href === '/') {
      return pathname === '/';
    }
    if (key === 'slots' || key === 'fish' || key === 'live' || key === 'sports' || key === 'hot' || key === 'new') {
      return pathname.includes('/games') && pathname.includes(key);
    }
    if (key === 'lottery') {
      return pathname.includes('/lottery');
    }
    return pathname.startsWith(href);
  };

  return (
    <section className="category-section">
      <div className="category-scroll">
        {allCategories.map((category) => {
          const active = isActive(category.href, category.key);
          
          return (
            <Link
              key={category.id}
              href={category.href}
              className={`category-item ${active ? 'active' : ''}`}
            >
              {category.isDefault ? (
                // Use icon for default categories
                <>
                  {category.id === 'home' && <FaHome className="category-icon" />}
                  {category.id === 'slots' && <FaGamepad className="category-icon" />}
                  {category.id === 'fish' && <FaFish className="category-icon" />}
                  {category.id === 'live' && <FaDice className="category-icon" />}
                  {category.id === 'lottery' && <FaGem className="category-icon" />}
                  {category.id === 'sports' && <FaTrophy className="category-icon" />}
                  {category.id === 'hot' && <FaFire className="category-icon" />}
                  {category.id === 'new' && <FaStar className="category-icon" />}
                </>
              ) : (
                // Use image for API categories
                <div className="category-image-container">
                  <Image
                    src={`${imgPrefix}${'image' in category ? category.image : ''}`}
                    alt={category.label}
                    width={24}
                    height={24}
                    className="category-image"
                  />
                </div>
              )}
              <span className="category-text">{category.label}</span>
            </Link>
          );
        })}
      </div>
    </section>
  );
};

export default CategorySection;
